import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const ParentDashboard = ({ onNavigate, userInfo }) => {
  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentData, setParentData] = useState({
    name: "Loading...",
    parentId: "...",
    relation: "Parent",
    email: "",
    phone: "",
    profileImage: null
  });

  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    rollNumber: "...",
    course: "...",
    department: "...",
    year: "...",
    batch: "...",
    cgpa: "0.00",
    attendancePercentage: 0
  });

  // Dashboard Data States
  const [dashboardStats, setDashboardStats] = useState({
    academic: { recentMarks: [], overallCGPA: "0.00", totalSubjects: 0 },
    attendance: { recentRecords: [], overallPercentage: 0, totalClasses: 0, presentClasses: 0, absentClasses: 0 },
    fees: { hasPendingFees: false, pendingAmount: "0", recentFees: [] },
    notifications: [],
    holidays: [],
    announcements: [],
    events: []
  });

  // Fetch Dashboard Data from API
  useEffect(() => {
    fetchDashboardData();
  }, [userInfo]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Starting API call to fetch parent dashboard data...');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');

      if (!token) {
        console.log('❌ No token found, using fallback data');
        setError('Authentication token not found. Using fallback data.');
        setParentData(prev => ({
          ...prev,
          name: userInfo?.name || "Parent User",
          parentId: userInfo?.id || "PARENT-001"
        }));
        setLoading(false);
        return;
      }

      const apiUrl = `${API_LINK}/api/parent/dashboard`;
      console.log('🌐 API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);

      const contentType = response.headers.get('content-type');
      console.log('📄 Content-Type:', contentType);

      if (!response.ok) {
        if (response.status === 401) {
          console.log('🔐 Authentication failed');
          setError('Session expired. Please login again.');
          handleLogout();
          return;
        } else if (response.status === 404) {
          console.log('🔍 API endpoint not found');
          setError('API endpoint not found. Please check server configuration.');
          setLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!contentType || !contentType.includes('application/json')) {
        console.log('❌ Response is not JSON:', contentType);
        setError('Server returned non-JSON response. Please check API configuration.');
        setLoading(false);
        return;
      }

      const result = await response.json();
      console.log('📦 API Response:', result);

      if (result.success && result.data) {
        const data = result.data;
        console.log('✅ Processing API data...');

        // Update parent data
        setParentData({
          name: data.parent?.name || userInfo?.name || "Parent",
          parentId: data.parent?.parentId || userInfo?.id || "PARENT-001",
          relation: data.parent?.relation || "Parent",
          email: data.parent?.email || "",
          phone: data.parent?.phone || "",
          profileImage: data.parent?.profileImage || null
        });

        // Update student data
        setStudentData({
          name: data.student?.name || "Student",
          studentId: data.student?.studentId || "STU-001",
          rollNumber: data.student?.rollNumber || "...",
          course: data.student?.course || "...",
          department: data.student?.department || "...",
          year: data.student?.year || "...",
          batch: data.student?.batch || "...",
          cgpa: data.student?.cgpa || "0.00",
          attendancePercentage: data.student?.attendancePercentage || 0
        });

        // Update dashboard stats
        setDashboardStats({
          academic: data.academic || { recentMarks: [], overallCGPA: "0.00", totalSubjects: 0 },
          attendance: data.attendance || { recentRecords: [], overallPercentage: 0, totalClasses: 0, presentClasses: 0, absentClasses: 0 },
          fees: data.fees || { hasPendingFees: false, pendingAmount: "0", recentFees: [] },
          notifications: data.notifications || [],
          holidays: data.holidays || [],
          announcements: data.announcements || [],
          events: data.events || []
        });

        console.log('✅ Parent dashboard data updated successfully');

      } else {
        console.log('❌ API returned error:', result.message);
        setError(result.message || 'Failed to fetch dashboard data');
      }

    } catch (error) {
      console.error('💥 Error fetching parent dashboard data:', error);

      if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
        setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
      } else if (error.message.includes('fetch')) {
        setError('Cannot connect to server. Please ensure the server is running');
      } else {
        setError(`Failed to load dashboard data: ${error.message}`);
      }

      if (userInfo) {
        console.log('🔄 Using fallback userInfo data');
        setParentData(prev => ({
          ...prev,
          name: userInfo.name || prev.name,
          parentId: userInfo.id || prev.parentId
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');
    sessionStorage.clear();
    localStorage.clear();

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    switch (page) {
      case 'Dashboard':
        // Stay on current page since this is the dashboard component
        break;
      case 'Academic Performance':
        onNavigate('parent-academic-performance');
        break;
      case 'Fee Status':
        onNavigate('parent-fees-status');
        break;
      case 'Holidays & Events':
        onNavigate('parent-holidays');
        break;
      case 'Emergency Alert':
        onNavigate('parent-emergency-alert');
        break;
      case 'Logout':
        handleLogout();
        break;
      default:
        console.log(`Navigation to ${page} clicked`);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Parent Portal...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Parent Portal
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="parent"
      onNavigate={handleNavigation}
      parentData={parentData}
      activePage="Dashboard"
    >
      {/* Fee Notification Bar */}
      <div style={{
        marginBottom: '20px',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#238548'
      }}>
        <div style={{
          width: '100%',
          height: '40px',
          overflow: 'hidden',
          backgroundColor: '#238548'
        }}>
          <div style={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            animation: 'marquee 15s linear infinite'
          }}>
            <span style={{ marginRight: '10px' }}>🔔</span>
            {dashboardStats.fees.hasPendingFees
              ? `Your child ${studentData.name} has pending fees of ₹${dashboardStats.fees.pendingAmount}. Please pay as soon as possible.`
              : `All fees for ${studentData.name} have been paid. No pending payments.`
            }
          </div>
        </div>
      </div>

      {/* Student Info Card */}
      <section style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h3 style={{
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          marginBottom: '1.5rem'
        }}>
          <span style={{ color: '#238548' }}>👨‍🎓</span>
          Student Information
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem'
        }}>
          <div>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{studentData.name}</h4>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Student ID:</strong> {studentData.studentId}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Roll Number:</strong> {studentData.rollNumber}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Course:</strong> {studentData.course}</p>
          </div>
          <div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Department:</strong> {studentData.department}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Year:</strong> {studentData.year}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Batch:</strong> {studentData.batch}</p>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {[
          {
            icon: '🎓',
            title: 'Academic Performance',
            value: studentData.cgpa,
            subtitle: 'Current CGPA'
          },
          {
            icon: '✅',
            title: 'Attendance',
            value: `${studentData.attendancePercentage}%`,
            subtitle: studentData.attendancePercentage >= 75 ? 'Good Standing' : 'Needs Improvement'
          },
          {
            icon: '💳',
            title: 'Fee Status',
            value: dashboardStats.fees.hasPendingFees ? `₹${dashboardStats.fees.pendingAmount}` : 'Paid',
            subtitle: dashboardStats.fees.hasPendingFees ? 'Amount Due' : 'All Fees Paid'
          },
          {
            icon: '📚',
            title: 'Total Subjects',
            value: dashboardStats.academic.totalSubjects,
            subtitle: 'Current Semester'
          }
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            transition: 'all 0.3s ease'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
            }}>
            <div style={{
              fontSize: '2.5rem',
              color: '#238548',
              marginBottom: '1rem'
            }}>
              {stat.icon}
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{stat.title}</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: '#238548',
              margin: '0.5rem 0'
            }}>
              {stat.value}
            </p>
            <p style={{ color: '#666', margin: 0 }}>
              {stat.subtitle}
            </p>
          </div>
        ))}
      </section>

      {/* Recent Academic Performance & Attendance */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Recent Academic Performance */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📈</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Academic Performance</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {dashboardStats.academic.recentMarks.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginBottom: '1rem'
                }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #ecf0f1' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', background: '#f4f7fa', fontWeight: 600, color: '#333' }}>Subject</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', background: '#f4f7fa', fontWeight: 600, color: '#333' }}>Total</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', background: '#f4f7fa', fontWeight: 600, color: '#333' }}>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardStats.academic.recentMarks.slice(0, 5).map((mark, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #ecf0f1' }}>
                        <td style={{ padding: '0.75rem', textAlign: 'left' }}>{mark.subject}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'left' }}>{mark.total}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'left' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontWeight: 500,
                            ...(mark.grade === 'A+' || mark.grade === 'A' ? {
                              backgroundColor: '#c8e6c9',
                              color: '#2e7d32'
                            } : mark.grade === 'B+' || mark.grade === 'B' ? {
                              backgroundColor: '#bbdefb',
                              color: '#1565c0'
                            } : {
                              backgroundColor: '#fff9c4',
                              color: '#f9a825'
                            })
                          }}>
                            {mark.grade}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No academic records found
              </p>
            )}
          </div>
        </div>

        {/* Attendance Summary */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>✅</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Attendance Summary</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              marginBottom: '1.5rem',
              borderBottom: '1px solid #ecf0f1',
              paddingBottom: '1.5rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#238548'
                }}>{dashboardStats.attendance.overallPercentage}%</div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}>Overall</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#238548'
                }}>{dashboardStats.attendance.presentClasses}</div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}>Present</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#238548'
                }}>{dashboardStats.attendance.absentClasses}</div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}>Absent</div>
              </div>
            </div>

            <h4 style={{
              margin: '1rem 0',
              color: '#333',
              fontSize: '1.2rem'
            }}>Recent Attendance</h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              {dashboardStats.attendance.recentRecords.slice(0, 5).map((record, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem',
                  background: '#f4f7fa',
                  borderRadius: '8px'
                }}>
                  <div>
                    <div style={{ fontWeight: 500, color: '#333' }}>{formatDate(record.date)}</div>
                    <div style={{ color: '#666', fontSize: '0.9rem' }}>{record.subject}</div>
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    ...(record.status === 'present' ? {
                      background: '#e8f5e9',
                      color: '#2e7d32'
                    } : {
                      background: '#ffebee',
                      color: '#c62828'
                    })
                  }}>
                    {record.status === 'present' ? 'Present' : 'Absent'}
                  </div>
                </div>
              ))}
              {dashboardStats.attendance.recentRecords.length === 0 && (
                <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>
                  No attendance records found
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Notifications & Events */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem'
      }}>
        {/* Notifications */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>🔔</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Notifications</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {dashboardStats.notifications.length > 0 ? dashboardStats.notifications.slice(0, 3).map((notification, index) => (
              <div key={index} style={{
                padding: '1rem',
                borderBottom: index < dashboardStats.notifications.length - 1 ? '1px solid #ecf0f1' : 'none'
              }}>
                <p style={{ margin: '0 0 0.5rem 0', color: '#333', fontWeight: 500 }}>{notification.message}</p>
                <p style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  color: '#999',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span>{formatDate(notification.date)}</span>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    ...(notification.type === 'urgent' ? {
                      background: '#e74c3c',
                      color: '#ffffff'
                    } : notification.type === 'important' ? {
                      background: '#3498db',
                      color: '#ffffff'
                    } : {
                      background: '#bdc3c7',
                      color: '#333'
                    })
                  }}>
                    {notification.type}
                  </span>
                </p>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No notifications available
              </p>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📅</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Upcoming Events</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {dashboardStats.events.length > 0 ? dashboardStats.events.slice(0, 3).map((event, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '1.5rem',
                padding: '1.5rem',
                borderBottom: index < dashboardStats.events.length - 1 ? '1px solid #ecf0f1' : 'none'
              }}>
                <div style={{
                  background: '#238548',
                  color: '#ffffff',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  minWidth: '80px'
                }}>
                  <div style={{ fontWeight: 'bold' }}>{new Date(event.date).getDate()}</div>
                  <div style={{ fontSize: '0.8rem' }}>
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{event.title}</h4>
                  <p style={{
                    margin: '0 0 0.5rem 0',
                    color: '#666',
                    fontSize: '0.9rem'
                  }}>
                    {event.description}
                  </p>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    background: '#f4f7fa',
                    color: '#666',
                    borderRadius: '15px',
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}>
                    {event.type}
                  </span>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No upcoming events
              </p>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ParentDashboard;